<template>
  <section class="lesson-kit">
    <!-- 선택한 부서/학기/과 요약 (style.css 의 .mall_main header 그리드를 피하려고 div 사용) -->
    <div class="lesson-kit__head">
      <!-- 콘텐츠 종류(division) 필터 -->
      <!-- 모바일은 두 줄로 나눠 함께 가로 스크롤, 그 이상은 줄 구분 없이 자연스럽게 줄바꿈 -->
      <div class="lesson-kit__chips" role="group" aria-label="콘텐츠 종류">
        <div
          v-for="(row, rowIdx) in chipRows"
          :key="rowIdx"
          class="lesson-kit__chip-row"
        >
          <button
            v-for="chip in row"
            :key="chip.division"
            type="button"
            class="lesson-kit__chip"
            :class="{ 'is-active': activeDivision === chip.division }"
            :aria-pressed="activeDivision === chip.division"
            @click="$emit('update:activeDivision', chip.division)"
          >
            {{ chip.label }}
            <span class="lesson-kit__chip-count">
              {{ chip.count.toLocaleString() }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 목록: division 구분 없이 한 그리드에, 종류는 썸네일 배지로 표시 -->
    <ul class="lesson-kit__grid" :class="{ 'is-loading': loading }">
      <li v-for="item in items" :key="`${item.division}-${item.content.id}`">
        <button
          type="button"
          class="lesson-kit__card"
          @click="$emit('open', item.content)"
        >
          <div class="lesson-kit__thumb">
            <img
              v-if="thumbnailOf(item.content)"
              :src="thumbnailOf(item.content)"
              :alt="item.content.title"
              loading="lazy"
            />
            <img
              v-else
              src="@/assets/img/null_img.jpg"
              :alt="item.content.title"
            />
            <!-- 특정 종류를 고른 경우엔 배지가 중복이므로 '전체'에서만 표시 -->
            <span
              v-if="!activeDivision && item.division"
              class="lesson-kit__badge"
            >
              {{ item.division }}
            </span>
          </div>

          <div class="lesson-kit__body">
            <span v-if="groupNamesOf(item.content)" class="lesson-kit__group">
              {{ groupNamesOf(item.content) }}
            </span>
            <h3 class="product_tt">{{ item.content.title }}</h3>
          </div>
        </button>
      </li>
    </ul>

    <pagination
      v-if="pageInfo && pageInfo.page && pageInfo.total > size"
      :pageInfo="pageInfo"
      :page="pageInfo.page"
      :size="size"
      @updatePage="(num) => $emit('updatePage', num)"
    />
  </section>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import Pagination from "@/components/Pagination.vue";

const props = defineProps({
  // 콘텐츠가 있는 division 칩 ([{ division, count }], 노출 순서대로)
  chips: {
    type: Array,
    default: () => [],
  },
  // '전체' 칩에 표시할 개수
  totalCount: {
    type: Number,
    default: 0,
  },
  // 선택한 division ('' 이면 전체)
  activeDivision: {
    type: String,
    default: "",
  },
  // 그리드에 보여줄 콘텐츠 ([{ division, content }], division 을 모르면 null)
  items: {
    type: Array,
    default: () => [],
  },
  // 페이지 정보 (없으면 페이지네이션 미노출)
  pageInfo: {
    type: Object,
    default: null,
  },
  size: {
    type: Number,
    default: 25,
  },
  // 페이지 이동 중 여부
  loading: {
    type: Boolean,
    default: false,
  },
  // 선택한 부서/학기/과 라벨 (예: ["영유아부", "5과정2학기", "23과"])
  labels: {
    type: Array,
    default: () => [],
  },
  // 콘텐츠 썸네일 주소를 돌려주는 함수
  thumbnailOf: {
    type: Function,
    default: () => null,
  },
});

defineEmits(["open", "update:activeDivision", "updatePage"]);

// '전체'를 포함한 칩을 앞/뒤 절반씩 두 줄로 나눔 (division '' 이 전체)
const chipRows = computed(() => {
  const allChips = [
    { division: "", label: "전체", count: props.totalCount },
    ...props.chips.map((chip) => ({ ...chip, label: chip.division })),
  ];
  const half = Math.ceil(allChips.length / 2);
  return [allChips.slice(0, half), allChips.slice(half)].filter(
    (row) => row.length > 0,
  );
});

/** 대상 부서명을 한 줄로 합친다 */
const groupNamesOf = (content) =>
  (content?.targetGroupList ?? [])
    .map((group) => group.groupNameValue)
    .filter(Boolean)
    .join(" · ");
</script>

<style scoped>
/* ── 디자인 토큰 (하나 바이블 페이지들과 동일) ── */
.lesson-kit {
  --hb-navy-900: #07235e;
  --hb-navy-700: #0c3388;
  --hb-navy-500: #1c4bb0;
  --hb-navy-300: #2f98e4;
  --hb-band-sky: #eff5fb;
  --hb-ink-700: #212121;
  --hb-ink-500: #525252;
  --hb-gray-200: #eaeaea;
  --hb-hairline: rgb(12 51 136 / 0.07);
  --hb-radius-lg: 1rem;
  --hb-radius-pill: 999px;
  --hb-shadow-sm: 0 2px 10px rgb(12 51 136 / 0.06);

  color: var(--hb-ink-700);
}

/* ── 요약 ── */
.lesson-kit__head {
  padding: 2rem 2.5rem;
  border: 1px solid var(--hb-hairline);
  border-radius: 1.25rem;
  background: var(--hb-band-sky);
}

.lesson-kit__eyebrow {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--hb-navy-500);
}

.lesson-kit__title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  margin-top: 0.375rem;
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.03em;
  color: var(--hb-navy-900);
  word-break: keep-all;
}

.lesson-kit__dot {
  margin: 0 0.5rem;
  color: var(--hb-navy-300);
}

.lesson-kit__desc {
  margin-top: 0.5rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--hb-ink-500);
  word-break: keep-all;
}

.lesson-kit__desc b {
  font-weight: 700;
  color: var(--hb-navy-700);
  white-space: nowrap;
}

/* ── 종류 필터 ── */
.lesson-kit__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* 줄 구분은 모바일에서만 사용 (그 외엔 칩이 바로 .lesson-kit__chips 의 항목이 됨) */
.lesson-kit__chip-row {
  display: contents;
}

.lesson-kit__chip {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.875rem;
  border: 1px solid rgb(12 51 136 / 0.12);
  border-radius: var(--hb-radius-pill);
  background: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--hb-ink-700);
  white-space: nowrap;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;
}

.lesson-kit__chip-count {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--hb-navy-300);
}

.lesson-kit__chip.is-active {
  border-color: var(--hb-navy-700);
  background: var(--hb-navy-700);
  color: #fff;
}

.lesson-kit__chip.is-active .lesson-kit__chip-count {
  color: rgb(255 255 255 / 0.75);
}

/* ── 콘텐츠 그리드 ── */
.lesson-kit__grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 2rem 1.5rem;
  margin-top: 2.5rem;
  padding: 0;
  list-style: none;
  transition: opacity 0.2s ease;
}

.lesson-kit__grid.is-loading {
  opacity: 0.5;
  pointer-events: none;
}

.lesson-kit__card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
}

.lesson-kit__thumb {
  position: relative;
  overflow: hidden;
  aspect-ratio: 3 / 2;
  border: 1px solid var(--hb-hairline);
  border-radius: var(--hb-radius-lg);
  background: var(--hb-gray-200);
}

.lesson-kit__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lesson-kit__card:focus-visible {
  outline: none;
}

.lesson-kit__card:focus-visible .lesson-kit__thumb {
  outline: 2px solid var(--hb-navy-500);
  outline-offset: 2px;
}

.lesson-kit__badge {
  position: absolute;
  top: 0.625rem;
  left: 0.625rem;
  padding: 0.25rem 0.625rem;
  border-radius: var(--hb-radius-pill);
  background: rgb(255 255 255 / 0.92);
  box-shadow: var(--hb-shadow-sm);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  color: var(--hb-navy-700);
}

.lesson-kit__body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.lesson-kit__group {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--hb-navy-300);
}

/* ── 반응형 ── */
@media (max-width: 1023px) {
  .lesson-kit__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .lesson-kit__head {
    padding: 0.875rem;
    border-radius: var(--hb-radius-lg);
  }

  /* 모바일은 칩을 두 줄로 나눠 함께 가로 스크롤 (스크롤바는 숨김) */
  .lesson-kit__chips {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    overflow-x: auto;
    margin-right: -1.25rem;
    scrollbar-width: none;
  }

  .lesson-kit__chips::-webkit-scrollbar {
    display: none;
  }

  .lesson-kit__chip-row {
    display: flex;
    gap: 0.5rem;
    padding-right: 1.25rem;
  }

  .lesson-kit__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem 0.75rem;
    margin-top: 1.75rem;
  }

  .lesson-kit__card {
    gap: 0.5rem;
  }
}
</style>
